/**
 * WebSocket error handling service
 * Provides comprehensive error handling and recovery strategies
 * Requirements: 9.1
 */

export interface ErrorInfo {
  /** Error category */
  category: 'network' | 'protocol' | 'server' | 'client' | 'unknown';
  /** Severity level */
  severity: 'low' | 'medium' | 'high' | 'critical';
  /** User-friendly title */
  title: string;
  /** Detailed message */
  message: string;
  /** Suggested recovery actions */
  recoveryActions: string[];
  /** Whether automatic retry is recommended */
  shouldRetry: boolean;
  /** Recommended retry delay in milliseconds */
  retryDelay?: number;
}

export class WebSocketErrorHandler {
  /**
   * Analyze WebSocket close event and provide error information
   */
  static analyzeCloseEvent(event: CloseEvent): ErrorInfo {
    const { code, reason } = event;

    switch (code) {
      case 1000:
        return {
          category: 'network',
          severity: 'low',
          title: 'Connection Closed',
          message: 'Connection closed normally',
          recoveryActions: ['Connection was closed normally'],
          shouldRetry: false,
        };

      case 1001:
        return {
          category: 'network',
          severity: 'medium',
          title: 'Server Unavailable',
          message: 'Server is going away or browser is navigating away',
          recoveryActions: ['Wait a moment and try reconnecting', 'Check if server is running'],
          shouldRetry: true,
          retryDelay: 2000,
        };

      case 1002:
        return {
          category: 'protocol',
          severity: 'high',
          title: 'Protocol Error',
          message: 'WebSocket protocol error occurred',
          recoveryActions: ['Check browser compatibility', 'Contact support if issue persists'],
          shouldRetry: false,
        };

      case 1003:
        return {
          category: 'protocol',
          severity: 'high',
          title: 'Unsupported Data',
          message: 'Server received unsupported data type',
          recoveryActions: ['Check audio format compatibility', 'Restart the session'],
          shouldRetry: false,
        };

      case 1006:
        return {
          category: 'network',
          severity: 'high',
          title: 'Connection Lost',
          message: 'Connection was lost unexpectedly',
          recoveryActions: [
            'Check your internet connection',
            'Try reconnecting',
            'Restart your browser if issue persists'
          ],
          shouldRetry: true,
          retryDelay: 1000,
        };

      case 1007:
        return {
          category: 'protocol',
          severity: 'high',
          title: 'Invalid Data',
          message: 'Server received invalid data format',
          recoveryActions: ['Restart the transcription session', 'Check microphone settings'],
          shouldRetry: false,
        };

      case 1008:
        return {
          category: 'server',
          severity: 'high',
          title: 'Policy Violation',
          message: 'Server policy violation occurred',
          recoveryActions: ['Check session configuration', 'Contact administrator'],
          shouldRetry: false,
        };

      case 1009:
        return {
          category: 'protocol',
          severity: 'medium',
          title: 'Message Too Large',
          message: 'Audio chunk size exceeds server limits',
          recoveryActions: ['Reduce audio chunk size', 'Check audio settings'],
          shouldRetry: false,
        };

      case 1011:
        return {
          category: 'server',
          severity: 'high',
          title: 'Server Error',
          message: 'Server encountered an unexpected condition',
          recoveryActions: ['Wait a moment and try again', 'Contact support if issue persists'],
          shouldRetry: true,
          retryDelay: 5000,
        };

      case 1012:
        return {
          category: 'server',
          severity: 'medium',
          title: 'Server Restarting',
          message: 'Server is restarting',
          recoveryActions: ['Wait for server to restart', 'Try reconnecting in a few moments'],
          shouldRetry: true,
          retryDelay: 10000,
        };

      case 1013:
        return {
          category: 'server',
          severity: 'medium',
          title: 'Server Overloaded',
          message: 'Server is temporarily overloaded',
          recoveryActions: ['Wait a moment and try again', 'Try again during off-peak hours'],
          shouldRetry: true,
          retryDelay: 15000,
        };

      case 1014:
        return {
          category: 'network',
          severity: 'high',
          title: 'Bad Gateway',
          message: 'Network gateway error occurred',
          recoveryActions: ['Check network connection', 'Try again in a few minutes'],
          shouldRetry: true,
          retryDelay: 5000,
        };

      case 1015:
        return {
          category: 'network',
          severity: 'critical',
          title: 'TLS Error',
          message: 'TLS handshake failed',
          recoveryActions: [
            'Check if HTTPS is properly configured',
            'Try refreshing the page',
            'Contact administrator'
          ],
          shouldRetry: false,
        };

      default:
        return {
          category: 'unknown',
          severity: 'medium',
          title: 'Connection Error',
          message: reason || `Connection closed with code ${code}`,
          recoveryActions: ['Try reconnecting', 'Check network connection'],
          shouldRetry: true,
          retryDelay: 3000,
        };
    }
  }

  /**
   * Analyze generic WebSocket error
   */
  static analyzeError(error: Event | Error): ErrorInfo {
    if (error instanceof Error) {
      return {
        category: 'client',
        severity: 'high',
        title: 'WebSocket Error',
        message: error.message || 'An unknown WebSocket error occurred',
        recoveryActions: ['Check network connection', 'Try reconnecting'],
        shouldRetry: true,
        retryDelay: 2000,
      };
    }

    return {
      category: 'unknown',
      severity: 'medium',
      title: 'Connection Error',
      message: 'An unknown connection error occurred',
      recoveryActions: ['Check network connection', 'Try reconnecting'],
      shouldRetry: true,
      retryDelay: 2000,
    };
  }

  /**
   * Get user-friendly error message for display
   */
  static getDisplayMessage(errorInfo: ErrorInfo): string {
    const actions = errorInfo.recoveryActions.slice(0, 2).join('. ');
    return `${errorInfo.title}: ${errorInfo.message}. ${actions}.`;
  }

  /**
   * Determine if error should trigger automatic reconnection
   */
  static shouldAutoReconnect(errorInfo: ErrorInfo, attemptCount: number, maxAttempts: number): boolean {
    if (attemptCount >= maxAttempts) {
      return false;
    }

    return errorInfo.shouldRetry && errorInfo.severity !== 'critical';
  }

  /**
   * Calculate retry delay with exponential backoff
   */
  static calculateRetryDelay(errorInfo: ErrorInfo, attemptCount: number): number {
    const baseDelay = errorInfo.retryDelay || 1000;
    const exponentialDelay = baseDelay * Math.pow(2, attemptCount);
    const maxDelay = 30000; // 30 seconds max
    
    return Math.min(exponentialDelay, maxDelay);
  }

  /**
   * Create error message for network connectivity issues
   */
  static createNetworkError(): ErrorInfo {
    return {
      category: 'network',
      severity: 'high',
      title: 'Network Error',
      message: 'Unable to connect to the transcription server',
      recoveryActions: [
        'Check your internet connection',
        'Verify the server is running',
        'Try again in a few moments'
      ],
      shouldRetry: true,
      retryDelay: 3000,
    };
  }

  /**
   * Create error message for authentication issues
   */
  static createAuthError(): ErrorInfo {
    return {
      category: 'server',
      severity: 'critical',
      title: 'Authentication Error',
      message: 'Failed to authenticate with the transcription service',
      recoveryActions: [
        'Check server configuration',
        'Verify AWS credentials',
        'Contact administrator'
      ],
      shouldRetry: false,
    };
  }

  /**
   * Create error message for audio format issues
   */
  static createAudioFormatError(details?: string): ErrorInfo {
    return {
      category: 'client',
      severity: 'medium',
      title: 'Audio Format Error',
      message: details || 'Audio format is not supported',
      recoveryActions: [
        'Check microphone settings',
        'Try a different audio format',
        'Restart the browser'
      ],
      shouldRetry: false,
    };
  }
}